import type { AuthTelegramResponse, PhotoUploadResponse } from '@telega/shared';
import { uk } from '../i18n/uk';
import { env } from '../lib/env';
import { UploadError } from '../lib/upload-error';
import { rawInitData } from '../telegram';

/** API error with the server's `{ code, message, details }` body. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let token: string | null = null;
let loginInFlight: Promise<AuthTelegramResponse> | null = null;

/** `fetch` that turns "failed to fetch" into an error the person can act on (no connection). */
async function send(input: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(input, init);
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError(0, 'network', uk.common.offline);
  }
}

async function parseError(res: Response): Promise<ApiError> {
  try {
    const body = (await res.json()) as { code?: string; message?: string; details?: unknown };
    return new ApiError(
      res.status,
      body.code ?? 'error',
      body.message ?? res.statusText,
      body.details,
    );
  } catch {
    return new ApiError(res.status, 'error', res.statusText);
  }
}

/** Exchanges Telegram initData for a session token (one request even if called concurrently). */
export function login(): Promise<AuthTelegramResponse> {
  loginInFlight ??= (async () => {
    const initData = rawInitData();
    if (!initData)
      throw new ApiError(401, 'outside_telegram', 'Відкрийте застосунок через Telegram');
    const res = await send(`${env.apiUrl}/auth/telegram`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ initData }),
    });
    if (!res.ok) throw await parseError(res);
    const body = (await res.json()) as AuthTelegramResponse;
    token = body.token;
    return body;
  })().finally(() => {
    loginInFlight = null;
  });
  return loginInFlight;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

/**
 * Authenticated JSON request; a `Blob` body is sent as is with its own type (a camera frame).
 * Re-authenticates once with fresh initData on 401 (D17).
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  if (!token) await login();
  const { body } = options;
  const blob = body instanceof Blob ? body : null;

  const attempt = () =>
    send(`${env.apiUrl}${path}`, {
      method: options.method ?? 'GET',
      headers: {
        authorization: `Bearer ${token}`,
        ...(body !== undefined
          ? { 'content-type': blob ? blob.type || 'image/jpeg' : 'application/json' }
          : {}),
      },
      ...(body !== undefined ? { body: blob ?? JSON.stringify(body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });

  let res = await attempt();
  if (res.status === 401) {
    token = null;
    await login();
    res = await attempt();
  }
  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

function errorFromText(status: number, text: string): ApiError {
  try {
    const body = JSON.parse(text) as { code?: string; message?: string; details?: unknown };
    if (body.message) return new ApiError(status, body.code ?? 'error', body.message, body.details);
  } catch {
    // not our JSON (a proxy page): fall through
  }
  return new ApiError(status, 'error', `Сервер відповів ${status}`);
}

/**
 * Sends one photo to the API as the request body (D38), reporting upload progress, which `fetch`
 * cannot. Re-authenticates once on 401 like `request`. No answer at all rejects with UploadError.
 */
export async function uploadPhoto(
  carId: string,
  photo: Blob,
  onProgress: (fraction: number) => void,
): Promise<PhotoUploadResponse> {
  if (!token) await login();

  const attempt = () =>
    new Promise<{ status: number; text: string }>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${env.apiUrl}/cars/${carId}/photos`);
      xhr.setRequestHeader('authorization', `Bearer ${token}`);
      xhr.setRequestHeader('content-type', 'image/jpeg');
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(e.loaded / e.total);
      };
      xhr.onload = () => resolve({ status: xhr.status, text: xhr.responseText ?? '' });
      xhr.onerror = () => reject(new UploadError('network'));
      xhr.ontimeout = () => reject(new UploadError('timeout'));
      xhr.timeout = 60_000;
      xhr.send(photo);
    });

  let res = await attempt();
  if (res.status === 401) {
    token = null;
    await login();
    res = await attempt();
  }
  if (res.status < 200 || res.status >= 300) throw errorFromText(res.status, res.text);
  return JSON.parse(res.text) as PhotoUploadResponse;
}

/** Test hook. */
export function resetSession(): void {
  token = null;
  loginInFlight = null;
}

/** Public link that answers with the photo as a file to save (D44); absolute, as `apiUrl` may not be. */
export function photoDownloadUrl(photoId: string): string {
  return new URL(`${env.apiUrl}/photos/${photoId}/download`, window.location.href).href;
}
